import React, { useContext, useEffect } from "react";
import { FirebaseContext } from "../contexts/FirebaseContext";
import { useCommonUI } from "../contexts/CommonUIContext";
import ManageLabelsDialog from "../components/ManageLabelsDialog";

// Shared "Edit Labels" entry point used by Home, Task detail, and Stats so the
// label + action-template management experience is identical everywhere.
// Returns a function that opens the single ManageLabelsDialog. `projectId` is
// the scope used when creating a non-global label/template (null = global only).
export function useManageLabels(projectId = null) {
  const {
    projects,
    labels,
    createLabel,
    updateLabel,
    deleteLabel,
    actionTemplates,
    startCollectingActionTemplates,
    createActionTemplate,
    updateActionTemplate,
    deleteActionTemplate,
  } = useContext(FirebaseContext);
  const { openModal, isVisible, modalContent } = useCommonUI();

  useEffect(() => {
    if (startCollectingActionTemplates) startCollectingActionTemplates();
  }, [startCollectingActionTemplates]);

  const build = () => (
    <ManageLabelsDialog
      labels={labels}
      projects={projects}
      projectId={projectId}
      createLabel={createLabel}
      updateLabel={updateLabel}
      deleteLabel={deleteLabel}
      actionTemplates={actionTemplates}
      createActionTemplate={createActionTemplate}
      updateActionTemplate={updateActionTemplate}
      deleteActionTemplate={deleteActionTemplate}
    />
  );

  // The modal is rendered by CommonUIProvider (above FirebaseProvider), so it
  // can't read FirebaseContext — it only gets a snapshot of labels/templates as
  // props at open time. That snapshot would go stale after a save (the user had
  // to reload to see it). While OUR dialog is open, re-push fresh props whenever
  // the live data changes. CommonUI renders a single same-type element, so React
  // reconciles it in place and ManageLabelsDialog keeps its internal state
  // (current view + in-progress form fields).
  useEffect(() => {
    if (isVisible && modalContent && modalContent.type === ManageLabelsDialog) {
      openModal(build());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labels, actionTemplates, projects]);

  return () => openModal(build());
}
